/**
 * The Z5/Z6 host's state (S5): which page registered tools, which tool is open (TAB STATE: the
 * preferences whitelist has no key for it, so a reload opens with the panel closed), what
 * opened it (focus goes back there), and the tool column's width for the split.
 *
 * Kept apart from ToolHost.jsx so QuoteSplit can read the column width without importing the
 * host (and its geometry) — no import cycle.
 */
import { createContext, createElement, useContext, useState, useSyncExternalStore } from 'react';

const INITIAL = Object.freeze({ registration: null, openId: null, opener: null, columnWidth: 0 });

export function createToolHostStore() {
  let state = INITIAL;
  const listeners = new Set();
  const set = (patch) => {
    const next = { ...state, ...patch };
    if (Object.keys(next).every((k) => next[k] === state[k])) return;
    state = next;
    listeners.forEach((fn) => fn());
  };
  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    setRegistration(registration) {
      set(registration ? { registration } : { registration: null, openId: null, opener: null, columnWidth: 0 });
    },
    open(openId, opener = null) { set({ openId, opener }); },
    toggle(openId, opener = null) {
      if (state.openId === openId) set({ openId: null, opener: null });
      else set({ openId, opener });
    },
    /** Close; by default focus returns to what opened it. */
    close({ returnFocus = true } = {}) {
      const { opener } = state;
      set({ openId: null, opener: null });
      if (returnFocus && opener && typeof opener.focus === 'function' && opener.isConnected) opener.focus();
    },
    setColumnWidth(columnWidth) { set({ columnWidth }); }
  };
}

const ToolHostContext = createContext(null);

/** MainLayout wraps the shell in this; pages register their tools through useToolHost. */
export function ToolHostProvider({ children }) {
  const [store] = useState(createToolHostStore);
  return createElement(ToolHostContext.Provider, { value: store }, children);
}

export const useToolHostStore = () => useContext(ToolHostContext);

const EMPTY_STORE = { subscribe: () => () => {}, getState: () => INITIAL };

export function useToolHostState() {
  const store = useToolHostStore() || EMPTY_STORE;
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

/** The open tool column's width (0: none, or the sheet). The split yields the list to it. */
export function useToolColumnWidth() {
  return useToolHostState().columnWidth;
}
